import { business } from "./content/business";
import { sourcingFaqs } from "./content/sourcingFaqs";

export const SITE_ORIGIN = "https://nxsupplychain.com";
export const SITE_NAME = "Nexus Supply Chain";
export const LOGO_PATH = "/brand/nsc-horizontal-2026.webp";
export const SOCIAL_IMAGE_PATH = "/brand/nexus-social-preview.png";
export const SOCIAL_IMAGE_WIDTH = "1200";
export const SOCIAL_IMAGE_HEIGHT = "630";
export const SOCIAL_IMAGE_ALT = "Nexus Supply Chain — Your procurement team on the ground in China";

export type SeoEntry = {
  title: string;
  description: string;
  serviceName?: string;
  serviceType?: string;
};

export const defaultSeo: SeoEntry = {
  title: "China Product Sourcing & Procurement | Nexus Supply Chain",
  description: "Your China-based procurement team for product sourcing, supplier verification, production, quality control and international shipping. Start a sourcing request.",
};

export const routeSeo: Record<string, SeoEntry> = {
  "/": defaultSeo,
  "/services": { title: "China Sourcing & Supply Chain Services | Nexus Supply Chain", description: "Explore product sourcing, supplier verification, product development, production management, quality inspection, consolidation and freight services from China." },
  "/process": { title: "Supply Chain Process | From Supplier to Shipment | Nexus", description: "See the Nexus Supply Chain process for supplier coordination, verification, inspection, consolidation, and freight from China." },
  "/how-nexus-works": { title: "How Nexus Supply Chain Works | Brief to Delivery", description: "See how Nexus coordinates product requirements, supplier research, sampling, production, quality control and international delivery from China." },
  "/why-nexus": { title: "Why Nexus Supply Chain | China-Based Product Operations", description: "Learn how China-based execution, supplier comparison, quality-first decisions and one connected team reduce supply-chain handoffs." },
  "/about": { title: "About Nexus Supply Chain | China Sourcing Team", description: "Meet Nexus Supply Chain founder Mamoon Rashid and learn how the China-based team coordinates sourcing, production, quality and logistics." },
  "/contact": { title: "Start a Sourcing Request | Nexus Supply Chain", description: "Share your product, supplier, production, inspection or shipping requirements with the Nexus Supply Chain team in China." },
  "/start-project": { title: "Start a Sourcing Request | Nexus Supply Chain", description: "Submit a sourcing request for product sourcing, supplier verification, production management, quality control, consolidation or freight from China." },
  "/services/sourcing": { title: "China Product Sourcing Service | Nexus Supply Chain", description: "Source products from China with supplier research, quotation comparison, screening, negotiation and sample coordination from Nexus Supply Chain.", serviceName: "China Product Sourcing", serviceType: "Product sourcing in China" },
  "/services/supplier-verification": { title: "Supplier Verification Services in China | Nexus", description: "Verify a China-based supplier's stated capabilities, production setup and fit before making a larger commercial commitment.", serviceName: "Supplier Verification", serviceType: "Supplier verification in China" },
  "/services/product-development": { title: "Product Sampling & Development in China | Nexus", description: "Coordinate product samples, technical revisions, specifications and approval with manufacturers in China before mass production.", serviceName: "Sampling and Product Development", serviceType: "Product sampling and development in China" },
  "/services/production-management": { title: "Production Management in China | Nexus Supply Chain", description: "Coordinate procurement requirements, supplier communication, production checkpoints and approvals with manufacturers in China.", serviceName: "Procurement and Production Management", serviceType: "Production management in China" },
  "/services/freight-forwarding": { title: "Freight Forwarding from China | Nexus Supply Chain", description: "Freight forwarding from China for wholesale inventory and commercial cargo. Sea, fast sea, air, express and selected China–Europe rail, with pickup, customs and final delivery support.", serviceName: "Freight Forwarding from China", serviceType: "Freight forwarding from China" },
  "/services/consolidation": { title: "Shipment Consolidation in China | Nexus Supply Chain", description: "Combine goods from multiple China-based suppliers into one organised shipment with clearer handling, lower freight duplication, and better visibility.", serviceName: "Shipment Consolidation", serviceType: "Shipment consolidation in China" },
  "/services/inspection": { title: "Quality Inspection Services in China | Nexus", description: "Check product quality, quantities, packaging, and defects before goods leave the factory with Nexus Supply Chain inspection support.", serviceName: "Quality Inspection", serviceType: "Quality inspection services in China" },
  "/services/china-trademark": { title: "China Trademark Registration Support | Nexus", description: "Coordinate China trademark information, documentation, classification inputs, and specialist registration support with Nexus Supply Chain.", serviceName: "China Trademark Support", serviceType: "China trademark registration support" },
  "/services/nda-nnn-agreements": { title: "NNN Agreement Support for China Factories | Nexus", description: "Organise factory details, confidential-information scope, and specialist support for NDA or NNN agreements with China-based manufacturers.", serviceName: "NDA and NNN Factory Agreement Support", serviceType: "NNN agreement support for China factories" },
  "/privacy": { title: "Privacy Notice | Nexus Supply Chain", description: "Read the Nexus Supply Chain privacy notice." },
  "/terms": { title: "Terms of Service | Nexus Supply Chain", description: "Read the Nexus Supply Chain terms of service." },
};

export const prerenderRoutes = Object.keys(routeSeo);

export function normalizePath(pathname: string) {
  if (pathname === "/") return pathname;
  return pathname.replace(/\/+$/, "");
}

export function canonicalFor(pathname: string) {
  return pathname === "/" ? `${SITE_ORIGIN}/` : `${SITE_ORIGIN}${normalizePath(pathname)}/`;
}

export function buildSchema(pathname: string, seo: SeoEntry) {
  const normalizedPath = normalizePath(pathname);
  const canonicalUrl = canonicalFor(pathname);
  const organizationId = `${SITE_ORIGIN}/#organization`;
  const websiteId = `${SITE_ORIGIN}/#website`;
  const organization = {
    "@type": "Organization",
    "@id": organizationId,
    name: SITE_NAME,
    alternateName: ["NEXUS", business.chineseName],
    legalName: business.legalName,
    telephone: business.telephone,
    url: `${SITE_ORIGIN}/`,
    logo: `${SITE_ORIGIN}${LOGO_PATH}`,
    description: defaultSeo.description,
    areaServed: "Worldwide",
    address: {
      "@type": "PostalAddress",
      streetAddress: business.streetAddress,
      addressLocality: "Guangzhou",
      addressRegion: "Guangdong",
      addressCountry: "CN",
    },
    founder: { "@type": "Person", name: "Mamoon Rashid", jobTitle: "Founder & CEO" },
  };

  const graph: Record<string, unknown>[] = [
    organization,
    {
      "@type": "WebSite",
      "@id": websiteId,
      url: `${SITE_ORIGIN}/`,
      name: SITE_NAME,
      publisher: { "@id": organizationId },
    },
  ];

  if (normalizedPath === "/contact") {
    graph.push({
      "@type": "ContactPage",
      "@id": `${canonicalUrl}#contact-page`,
      url: canonicalUrl,
      name: seo.title,
      description: seo.description,
      isPartOf: { "@id": websiteId },
      about: { "@id": organizationId },
    });
  }

  if (seo.serviceName) {
    graph.push({
      "@type": "Service",
      "@id": `${canonicalUrl}#service`,
      name: seo.serviceName,
      serviceType: seo.serviceType ?? seo.serviceName,
      description: seo.description,
      url: canonicalUrl,
      areaServed: "Worldwide",
      provider: { "@id": organizationId },
    });
    graph.push({
      "@type": "BreadcrumbList",
      "@id": `${canonicalUrl}#breadcrumb`,
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_ORIGIN}/` },
        { "@type": "ListItem", position: 2, name: "Services", item: `${SITE_ORIGIN}/services/` },
        { "@type": "ListItem", position: 3, name: seo.serviceName, item: canonicalUrl },
      ],
    });
  }

  if (normalizedPath === "/services/sourcing") {
    graph.push({
      "@type": "FAQPage",
      "@id": `${canonicalUrl}#faq`,
      mainEntity: sourcingFaqs.map((faq) => ({
        "@type": "Question",
        name: faq.q,
        acceptedAnswer: { "@type": "Answer", text: faq.a },
      })),
    });
  }

  return { "@context": "https://schema.org", "@graph": graph };
}
